import React, { useState, useEffect, useCallback } from "react";
import { Radio } from "./ui/Radio";
import { Heading } from "./ui/Heading";
import { Stack } from "./ui/Stack";
import { Card } from "./ui/Card";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";
import { Text } from "./ui/Text";
import { Price } from "./ui/Price";
import { useTranslation } from "react-i18next";
import { useStore } from "../context/StoreContext";
import { useApi } from "../context/ApiContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { AddressForm, Address } from "./AddressForm";

interface ShippingRate {
 id: string;
 name: string;
 price: number;
 estimatedDays: {
  min: number;
  max: number;
 };
 carrierProvider?: string;
 carrierService?: string;
 isDefault?: boolean;
}

interface SavedAddress {
 _id: string;
 firstName: string;
 lastName: string;
 company?: string;
 address1: string;
 address2?: string;
 city: string;
 province?: string;
 postalCode: string;
 country: string;
 phone?: string;
 type: "shipping" | "billing" | "both";
 isDefault?: boolean;
}

interface CheckoutShippingProps {
 shippingAddress: Address;
 billingAddress: Address;
 useSameAddress: boolean;
 shippingRates: ShippingRate[];
 selectedShipping: string;
 onShippingAddressChange: (address: Address) => void;
 onBillingAddressChange: (address: Address) => void;
 onUseSameAddressChange: (value: boolean) => void;
 onShippingSelect: (id: string) => void;
 onNext: () => void;
 onBack: () => void;
 onErrors?: (errors: Record<string, string>) => void;
 errors?: Record<string, string>;
 onShippingAddressSelect?: (addressId: string) => void;
 onBillingAddressSelect?: (addressId: string) => void;
 selectedShippingAddressId?: string | null;
 selectedBillingAddressId?: string | null;
 isDigitalOnlyOrder?: boolean; // Explicit flag to indicate digital-only orders
 loading?: boolean; // Loading state for shipping rates
}

export const CheckoutShipping: React.FC<CheckoutShippingProps> = ({
 shippingAddress,
 billingAddress,
 useSameAddress,
 shippingRates,
 selectedShipping,
 onShippingAddressChange,
 onBillingAddressChange,
 onUseSameAddressChange,
 onShippingSelect,
 onNext,
 onBack,
 onErrors,
 errors = {},
 onShippingAddressSelect,
 onBillingAddressSelect,
 selectedShippingAddressId,
 selectedBillingAddressId,
 isDigitalOnlyOrder = false,
 loading = false,
}) => {
 const { t } = useTranslation();
 const { isPluginEnabled } = useStore();
 const isOnlinePaymentEnabled = isPluginEnabled("online-payment");
 const { api } = useApi();
 const { user } = useAuth();
 const { currency } = useCurrency();
 const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
 const [savedBillingAddresses, setSavedBillingAddresses] = useState<SavedAddress[]>([]);
 const [useSavedBilling, setUseSavedBilling] = useState(false);
 const [showNewShippingForm, setShowNewShippingForm] = useState(true); // Show form by default, will be hidden if saved addresses exist
 const [showNewBillingForm, setShowNewBillingForm] = useState(false);
 const [hasInitializedShipping, setHasInitializedShipping] = useState(false);

 const handleSelectShippingAddress = useCallback((addressId: string) => {
  const address = savedAddresses.find((a) => a._id === addressId);
  if (address && onShippingAddressSelect) {
   onShippingAddressSelect(addressId);
   onShippingAddressChange({
    firstName: address.firstName,
    lastName: address.lastName,
    company: address.company || "",
    address1: address.address1,
    address2: address.address2 || "",
    city: address.city,
    province: address.province || "",
    postalCode: address.postalCode,
    country: address.country,
    phone: address.phone || "",
   });
   setShowNewShippingForm(false);
   setHasInitializedShipping(true); // Mark as initialized after user selection
  }
 }, [savedAddresses, onShippingAddressSelect, onShippingAddressChange]);

 // Helper function to auto-select default or first address for shipping
 const autoSelectAddress = useCallback((addressList: SavedAddress[]) => {
  if (addressList.length === 0 || !onShippingAddressSelect) {
   return;
  }

  // Try to find default address first
  const defaultAddress = addressList.find((addr: SavedAddress) => addr.isDefault);
  const addressToSelect = defaultAddress || addressList[0];

  if (addressToSelect) {
   onShippingAddressSelect(addressToSelect._id);
   onShippingAddressChange({
    firstName: addressToSelect.firstName,
    lastName: addressToSelect.lastName,
    company: addressToSelect.company || "",
    address1: addressToSelect.address1,
    address2: addressToSelect.address2 || "",
    city: addressToSelect.city,
    province: addressToSelect.province || "",
    postalCode: addressToSelect.postalCode,
    country: addressToSelect.country,
    phone: addressToSelect.phone || "",
   });
  }
 }, [onShippingAddressSelect, onShippingAddressChange]);

 // Helper function to auto-select default or first billing address
 const autoSelectBillingAddress = useCallback((addressList: SavedAddress[]) => {
  if (addressList.length === 0 || !onBillingAddressSelect) {
   return;
  }

  // Try to find default address first
  const defaultAddress = addressList.find((addr: SavedAddress) => addr.isDefault);
  const addressToSelect = defaultAddress || addressList[0];

  if (addressToSelect) {
   onBillingAddressSelect(addressToSelect._id);
   onBillingAddressChange({
    firstName: addressToSelect.firstName,
    lastName: addressToSelect.lastName,
    company: addressToSelect.company || "",
    address1: addressToSelect.address1,
    address2: addressToSelect.address2 || "",
    city: addressToSelect.city,
    province: addressToSelect.province || "",
    postalCode: addressToSelect.postalCode,
    country: addressToSelect.country,
    phone: addressToSelect.phone || "",
   });
   setUseSavedBilling(true);
  }
 }, [onBillingAddressSelect, onBillingAddressChange]);

 const fetchSavedAddresses = useCallback(async () => {
  try {
   // For billing-only mode (digital orders), fetch billing addresses instead of shipping
   // Use explicit isDigitalOnlyOrder prop instead of relying on shippingRates.length
   // because shippingRates might be empty during loading for non-digital orders
   const isBillingOnly = isDigitalOnlyOrder && useSameAddress;
   const addressType = isBillingOnly ? "billing" : "shipping";
   const response = await api.get(`/addresses?type=${addressType}`);
   const addresses = response.data || [];
   setSavedAddresses(addresses);
   
   // Only auto-hide form on initial load (when not yet initialized)
   // If user has explicitly chosen to show form, don't override that choice
   if (!hasInitializedShipping) {
    // If there are saved addresses, show them by default (hide the form)
    if (addresses.length > 0) {
     setShowNewShippingForm(false);
    } else {
     // If no saved addresses, show the form
     setShowNewShippingForm(true);
    }
   }
   
   // Auto-select default address on initial load if available and not already selected
   const defaultAddress = addresses.find((addr: SavedAddress) => addr.isDefault);
   if (defaultAddress && !selectedShippingAddressId && !hasInitializedShipping) {
    // Find address in the newly fetched addresses
    const address = addresses.find((a: SavedAddress) => a._id === defaultAddress._id);
    if (address && onShippingAddressSelect) {
     onShippingAddressSelect(defaultAddress._id);
     onShippingAddressChange({
      firstName: address.firstName,
      lastName: address.lastName,
      company: address.company || "",
      address1: address.address1,
      address2: address.address2 || "",
      city: address.city,
      province: address.province || "",
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone || "",
     });
     setHasInitializedShipping(true);
    }
   } else if (addresses.length > 0 && !selectedShippingAddressId && !hasInitializedShipping) {
    // If no default address but addresses exist, select the first one
    const firstAddress = addresses[0];
    if (firstAddress && onShippingAddressSelect) {
     onShippingAddressSelect(firstAddress._id);
     onShippingAddressChange({
      firstName: firstAddress.firstName,
      lastName: firstAddress.lastName,
      company: firstAddress.company || "",
      address1: firstAddress.address1,
      address2: firstAddress.address2 || "",
      city: firstAddress.city,
      province: firstAddress.province || "",
      postalCode: firstAddress.postalCode,
      country: firstAddress.country,
      phone: firstAddress.phone || "",
     });
     setHasInitializedShipping(true);
    }
   }
  } catch (err) {
   console.error("Error fetching addresses:", err);
   // On error, show the form
   setShowNewShippingForm(true);
  }
 }, [api, selectedShippingAddressId, hasInitializedShipping, onShippingAddressSelect, onShippingAddressChange, isDigitalOnlyOrder, useSameAddress]);

 // Fetch billing addresses separately when needed
 const fetchSavedBillingAddresses = useCallback(async () => {
  try {
   const response = await api.get("/addresses?type=billing");
   const addresses = response.data || [];
   setSavedBillingAddresses(addresses);
  } catch (err) {
   console.error("Error fetching billing addresses:", err);
  }
 }, [api]);

 useEffect(() => {
  if (user) {
   fetchSavedAddresses();
   // Also fetch billing addresses if not in billing-only mode (for separate billing section)
   const isBillingOnly = isDigitalOnlyOrder && useSameAddress;
   if (!isBillingOnly) {
    fetchSavedBillingAddresses();
   }
  } else {
   // Reset initialization flag when user logs out
   setHasInitializedShipping(false);
   // Show form if user is not logged in
   setShowNewShippingForm(true);
   setSavedAddresses([]);
   setSavedBillingAddresses([]);
  }
 }, [user, fetchSavedAddresses, fetchSavedBillingAddresses, isDigitalOnlyOrder, useSameAddress]);

 // Fetch billing addresses when user unchecks "Use same address for billing"
 useEffect(() => {
  const isBillingOnlyMode = isDigitalOnlyOrder && useSameAddress;
  if (user && !useSameAddress && !isBillingOnlyMode && savedBillingAddresses.length === 0) {
   fetchSavedBillingAddresses();
  }
 }, [user, useSameAddress, fetchSavedBillingAddresses, isDigitalOnlyOrder, savedBillingAddresses.length]);

 const handleSelectBillingAddress = (addressId: string) => {
  // Use appropriate address list based on mode
  const isBillingOnlyMode = isDigitalOnlyOrder && useSameAddress;
  const addressList = isBillingOnlyMode ? savedAddresses : savedBillingAddresses;
  const address = addressList.find((a) => a._id === addressId);
  if (address && onBillingAddressSelect) {
   onBillingAddressSelect(addressId);
   onBillingAddressChange({
    firstName: address.firstName,
    lastName: address.lastName,
    company: address.company || "",
    address1: address.address1,
    address2: address.address2 || "",
    city: address.city,
    province: address.province || "",
    postalCode: address.postalCode,
    country: address.country,
    phone: address.phone || "",
   });
   setUseSavedBilling(true);
   setShowNewBillingForm(false);
  }
 };

 const handleUseNewShippingAddress = () => {
  // Set initialization flag first to prevent fetchSavedAddresses from resetting form visibility
  setHasInitializedShipping(true);
  setShowNewShippingForm(true);
  // Clear the selected address after setting the flags
  if (onShippingAddressSelect) {
   onShippingAddressSelect("");
  }
 };

 const handleUseNewBillingAddress = () => {
  setUseSavedBilling(false);
  setShowNewBillingForm(true);
  if (onBillingAddressSelect) {
   onBillingAddressSelect("");
  }
 };
 // Helper to extract errors without prefix for AddressForm
 const getShippingErrors = (): Record<string, string> => {
  const shippingErrors: Record<string, string> = {};
  Object.keys(errors).forEach((key) => {
   if (key.startsWith("shipping_")) {
    const fieldName = key.replace("shipping_", "");
    shippingErrors[fieldName] = errors[key];
   } else if (!key.startsWith("billing_") && !key.includes("shippingMethod")) {
    // Also include non-prefixed errors that might be for shipping
    shippingErrors[key] = errors[key];
   }
  });
  return shippingErrors;
 };

 // Helper to extract errors without prefix for AddressForm
 const getBillingErrors = (): Record<string, string> => {
  const billingErrors: Record<string, string> = {};
  Object.keys(errors).forEach((key) => {
   if (key.startsWith("billing_")) {
    const fieldName = key.replace("billing_", "");
    billingErrors[fieldName] = errors[key];
   }
  });
  return billingErrors;
 };

 const validateShipping = (): {
  isValid: boolean;
  errors: Record<string, string>;
 } => {
  const newErrors: Record<string, string> = {};
  const required = [
   { field: "firstName", label: "First name" },
   { field: "lastName", label: "Last name" },
   { field: "address1", label: "Address" },
   { field: "city", label: "City" },
   { field: "postalCode", label: "Postal code" },
   { field: "country", label: "Country" },
  ];

  // Validate shipping address
  required.forEach(({ field, label }) => {
   const value = shippingAddress[field as keyof Address];
   if (!value || (typeof value === "string" && !value.trim())) {
    newErrors[`shipping_${field}`] = `${label} is required`;
   }
  });

  // Validate billing address if different
  if (!useSameAddress) {
   required.forEach(({ field, label }) => {
    const value = billingAddress[field as keyof Address];
    if (!value || (typeof value === "string" && !value.trim())) {
     newErrors[`billing_${field}`] =
      `Billing ${label.toLowerCase()} is required`;
    }
   });
  }

  // Validate shipping method
  // For non-billing-only orders, shipping method is required
  if (!isBillingOnly) {
   // If shipping rates are available but none selected, require selection
   if (shippingRates.length > 0 && !selectedShipping) {
    newErrors["shippingMethod"] = "Please select a shipping method";
   }
   // If not loading and no shipping rates available, show error
   if (!loading && shippingRates.length === 0) {
    newErrors["shippingMethod"] = "No shipping methods available for this location";
   }
  }

  // Update errors in parent component
  if (onErrors) {
   onErrors(newErrors);
  }
  return {
   isValid: Object.keys(newErrors).length === 0,
   errors: newErrors,
  };
 };

 const handleNext = (e: React.MouseEvent) => {
  e.preventDefault();
  const { isValid } = validateShipping();
  if (isValid) {
   onNext();
  } else {
   // Scroll to first error if any
   const firstError = document.querySelector('[class*="border-red"]');
   if (firstError) {
    firstError.scrollIntoView({ behavior: "smooth", block: "center" });
   }
  }
 };

 // Check if this is for digital-only orders
 // Use explicit isDigitalOnlyOrder prop instead of relying on shippingRates.length
 // because shippingRates might be empty during loading for non-digital orders
 const isBillingOnly = isDigitalOnlyOrder && useSameAddress;
 
 // Use appropriate address list for billing section
 const billingAddressesToShow = isBillingOnly ? savedAddresses : savedBillingAddresses;

 return (
  <Card padding="lg">
   <Stack spacing="md">
    <Heading size="lg">{isBillingOnly ? t("checkout.billingInformation") : t("checkout.shippingInformation")}</Heading>

    <div>
     <div className="flex items-center justify-between mb-4">
      <Heading size="md">{isBillingOnly ? t("checkout.billingAddress") : t("checkout.shippingAddress")}</Heading>
      {user && savedAddresses.length > 0 && (
       <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
         if (showNewShippingForm) {
          // Currently showing form, switch to saved addresses
          setShowNewShippingForm(false);
          // Auto-select default or first address if none is selected
          if (!selectedShippingAddressId && savedAddresses.length > 0) {
           autoSelectAddress(savedAddresses);
          }
         } else {
          // Currently showing saved addresses, switch to form
          handleUseNewShippingAddress();
         }
        }}
       >
        {showNewShippingForm ? t("checkout.useSavedAddress") : t("checkout.addNewAddress")}
       </Button>
      )}
     </div>

     {user && savedAddresses.length > 0 && !showNewShippingForm && (
      <div className="mb-4 space-y-2">
       {savedAddresses.map((address) => (
        <Card
         key={address._id}
         padding="md"
         className={`cursor-pointer border-2 transition-colors ${
          selectedShippingAddressId === address._id
           ? "border-store-accent bg-store-secondary bg-opacity-20"
           : "border-gray-200 hover:border-gray-300"
         }`}
         onClick={() => handleSelectShippingAddress(address._id)}
        >
         <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold">
             {address.firstName} {address.lastName}
             {address.isDefault && (
              <span className="ml-2 text-xs bg-store-secondary text-white px-2 py-1 rounded">{t("checkout.default")}</span>
             )}
            </div>
           <div className="text-sm text-gray-600 mt-1">
            {address.address1}
            {address.address2 && `, ${address.address2}`}
           </div>
           <div className="text-sm text-gray-600">
            {address.city}, {address.province} {address.postalCode}
           </div>
           <div className="text-sm text-gray-600">{address.country}</div>
          </div>
          <Radio
           checked={selectedShippingAddressId === address._id}
           onChange={() => handleSelectShippingAddress(address._id)}
          />
         </div>
        </Card>
       ))}
      </div>
     )}

     {showNewShippingForm && (
      <AddressForm
       address={{
        firstName: shippingAddress.firstName,
        lastName: shippingAddress.lastName,
        company: shippingAddress.company || "",
        address1: shippingAddress.address1,
        address2: shippingAddress.address2 || "",
        city: shippingAddress.city,
        province: shippingAddress.province || "",
        postalCode: shippingAddress.postalCode,
        country: shippingAddress.country,
        phone: shippingAddress.phone || "",
       }}
       onChange={(addr) => {
        onShippingAddressChange({
         firstName: addr.firstName,
         lastName: addr.lastName,
         company: addr.company || "",
         address1: addr.address1,
         address2: addr.address2 || "",
         city: addr.city,
         province: addr.province || "",
         postalCode: addr.postalCode,
         country: addr.country,
         phone: addr.phone || "",
        });
       }}
       showType={false}
       showDefault={false}
       errors={getShippingErrors()}
      />
     )}

    </div>

    {/* Hide "Use same address for billing" checkbox for digital-only orders (billing-only mode) */}
    {!isBillingOnly && (
     <div>
      <Radio
       label={t("checkout.useSameAddress")}
       checked={useSameAddress}
       onChange={(e) => {
        onUseSameAddressChange(e.target.checked);
        if (e.target.checked) {
         setShowNewBillingForm(false);
         setUseSavedBilling(false);
        }
       }}
      />
     </div>
    )}

    {/* Hide separate billing address section for digital-only orders (billing-only mode) */}
    {!useSameAddress && !isBillingOnly && (
     <div>
      <div className="flex items-center justify-between mb-4">
       <Heading size="md">Billing Address</Heading>
       {user && billingAddressesToShow.length > 0 && (
        <Button
         type="button"
         variant="outline"
         size="sm"
         onClick={() => {
          if (useSavedBilling) {
           handleUseNewBillingAddress();
          } else {
           // If currently showing form, switch to saved addresses and auto-select
           if (showNewBillingForm) {
            setShowNewBillingForm(false);
            // Auto-select default or first address if none is selected
            if (!selectedBillingAddressId && billingAddressesToShow.length > 0) {
             autoSelectBillingAddress(billingAddressesToShow);
            }
           } else {
            // Currently showing saved addresses, switch to form
            setShowNewBillingForm(true);
           }
          }
         }}
        >
         {useSavedBilling ? "Use New Address" : showNewBillingForm ? "Use Saved Address" : "Add New Address"}
        </Button>
       )}
      </div>

      {user && billingAddressesToShow.length > 0 && !showNewBillingForm && (
       <div className="mb-4 space-y-2">
        {billingAddressesToShow.map((address) => (
         <Card
          key={address._id}
          padding="md"
          className={`cursor-pointer border-2 transition-colors ${
           selectedBillingAddressId === address._id
            ? "border-store-accent bg-store-secondary bg-opacity-20"
            : "border-gray-200 hover:border-gray-300"
          }`}
          onClick={() => handleSelectBillingAddress(address._id)}
         >
          <div className="flex items-start justify-between">
           <div>
            <div className="font-semibold">
             {address.firstName} {address.lastName}
             {address.isDefault && (
              <span className="ml-2 text-xs bg-store-secondary text-white px-2 py-1 rounded">{t("checkout.default")}</span>
             )}
            </div>
            <div className="text-sm text-gray-600 mt-1">
             {address.address1}
             {address.address2 && `, ${address.address2}`}
            </div>
            <div className="text-sm text-gray-600">
             {address.city}, {address.province} {address.postalCode}
            </div>
            <div className="text-sm text-gray-600">{address.country}</div>
           </div>
           <Radio
            checked={selectedBillingAddressId === address._id}
            onChange={() => handleSelectBillingAddress(address._id)}
           />
          </div>
         </Card>
        ))}
       </div>
      )}

      {showNewBillingForm && (
       <AddressForm
        address={{
         firstName: billingAddress.firstName,
         lastName: billingAddress.lastName,
         company: billingAddress.company || "",
         address1: billingAddress.address1,
         address2: billingAddress.address2 || "",
         city: billingAddress.city,
         province: billingAddress.province || "",
         postalCode: billingAddress.postalCode,
         country: billingAddress.country,
         phone: billingAddress.phone || "",
        }}
        onChange={(addr) => {
         onBillingAddressChange({
          firstName: addr.firstName,
          lastName: addr.lastName,
          company: addr.company || "",
          address1: addr.address1,
          address2: addr.address2 || "",
          city: addr.city,
          province: addr.province || "",
          postalCode: addr.postalCode,
          country: addr.country,
          phone: addr.phone || "",
         });
        }}
        showType={false}
        showDefault={false}
        errors={getBillingErrors()}
       />
      )}
     </div>
    )}

    {!isBillingOnly && (
     <div>
      <Heading size="md" className="mb-4">
       {t("checkout.shippingMethod")}
      </Heading>
      <Stack spacing="sm">
       {loading ? (
        <p className="text-gray-500">{t("common.loading")}</p>
       ) : shippingRates.length === 0 ? (
        <Alert variant="warning">
         <Text size="sm" weight="semibold" className="mb-1">
          {t("checkout.noShippingMethodsConfigured")}
         </Text>
         <Text size="sm">
          {shippingAddress.country
            ? t("checkout.shippingMethodsNotConfiguredForCountry", { country: shippingAddress.country })
            : t("checkout.shippingMethodsNotConfigured")
          }
         </Text>
        </Alert>
       ) : (
        shippingRates.map((rate) => (
         <div key={rate.id} className="space-y-1">
          <Radio
           label={
            <div className="flex flex-col">
             <div className="flex items-center gap-2">
              <span>{rate.name}</span>
              {rate.isDefault && (
               <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                {t("admin.shipping.isDefault") || "Default"}
               </span>
              )}
              {rate.carrierProvider && (
               <span className="text-xs text-gray-500">
                ({rate.carrierProvider.toUpperCase()})
               </span>
              )}
             </div>
             <div className="text-sm text-gray-600 mt-0.5 flex items-center gap-1">
              <span>{rate.estimatedDays.min}-{rate.estimatedDays.max} {t("checkout.days")} • {t("checkout.price")}:</span>
              <Price amount={rate.price} currency={currency} size="sm" className="inline" />
             </div>
            </div>
           }
           checked={selectedShipping === rate.id}
           onChange={() => onShippingSelect(rate.id)}
          />
         </div>
        ))
       )}
      </Stack>
     </div>
    )}

    <Stack direction="row" spacing="md" justify="end">
     <Button
      variant="outline"
      onClick={onBack}
     >
      {t("checkout.back")}
     </Button>
     <Button
      onClick={handleNext}
      disabled={!validateShipping().isValid}
     >
      {isOnlinePaymentEnabled ? t("checkout.continueToPayment") : t("checkout.next")}
     </Button>
    </Stack>
   </Stack>
  </Card>
 );
};
